/**
 * How a workflow is doing: runs by outcome, goal conversion (goal reached
 * over enrolled), and how many runs reached each step, drawn as the step
 * tree with each branch's Yes and No split and each wait's matched and
 * timed out. When it sends email, how its emails did (`/email-stats`):
 * sent, delivered, opened, clicked and the rest, a line per day, each send
 * step's figures and clicks per link. Below, its enrolments from segments
 * and their schedules.
 *
 * Every figure is the server's (`/analytics`, `/enrolments`); nothing is
 * worked out here but the percentages.
 */
import { useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Box, Card, Group, Loader, Progress, SegmentedControl, Stack, Table, Text, Tooltip,
} from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartHover, XAxis, YAxis } from 'recharts';
import {
  useEnrolEdits, useEnrolments, useWorkflowAnalytics, useWorkflowEmailStats, type WorkflowStep, type WorkflowStepReach,
} from '../../api/workflows';
import {
  CHART_INK, CHART_POSITIVE, GRID_PROPS, LINE_CURSOR, MONO_SCALE, X_AXIS_PROPS, Y_AXIS_PROPS, chartAnimation,
  formatCompactNumber,
} from '../../charts';
import { formatNumber, TOKENS } from '../../theme';
import { ChartTooltip, LegendItem, StatGrid, StatTile } from '../../ui';
import { useVocab } from '../../vocab';
import { allSteps, stepLabel, sendsEmail, waitSentence, waitUntilSentence, cfg } from './workflowModel';

const pct = (n: number) => `${Math.round(n * 100)}%`;

export function AnalyticsPanel({ workflowId, steps, canManage }: { workflowId: string; steps: WorkflowStep[]; canManage: boolean }) {
  const v = useVocab();
  const a = useWorkflowAnalytics(workflowId);
  if (a.error) return <Alert color="red" variant="light">{a.error.message}</Alert>;
  if (!a.data) return <Group justify="center" p="lg"><Loader size="sm" /></Group>;
  const d = a.data;
  const reach = new Map(d.steps.map((s) => [s.step_id, s]));
  const tiles: [string, number, string?][] = [
    ['Enrolled', d.enrolled, d.enrolled_from_segments ? `${formatNumber(d.enrolled_from_segments)} from segments` : 'past their conditions'],
    ['Active', d.active, 'on a step now'],
    ['Waiting', d.waiting, 'for a time or an event'],
    ['Goal reached', d.goal_reached, d.goal_conversion !== null && d.goal_conversion !== undefined ? `${pct(d.goal_conversion)} of enrolled` : 'no goal reached yet'],
    ['Exited', d.exited, 'an exit rule was met'],
    ['Failed', d.failed, 'see the run log'],
  ];
  return (
    <Stack gap="md" data-testid="workflow-analytics">
      <StatGrid columns={6}>
        {tiles.map(([label, value, caption], i) => (
          <StatTile key={label} label={label} value={value} format={(n) => formatNumber(Math.round(n))} caption={caption} index={i}
            meter={label === 'Goal reached' && d.goal_conversion != null ? d.goal_conversion : undefined} />
        ))}
      </StatGrid>
      <Card padding="sm" withBorder>
        <Stack gap="xs">
          <Text size="sm" fw={600}>{`How far ${v.many('run')} got`}</Text>
          <Text size="xs" c="dimmed">{`Each step of this version, with how many ${v.many('run')} reached it.`}</Text>
          <ReachList steps={steps} reach={reach} enrolled={d.enrolled} depth={0} />
        </Stack>
      </Card>
      {sendsEmail(steps) && <EmailSection workflowId={workflowId} steps={steps} />}
      <Enrolments workflowId={workflowId} canManage={canManage} />
    </Stack>
  );
}

const DAY_RANGES = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

/** A rate as on the Emails overview: one decimal under 10%, '-' with nothing to measure against. */
function rate(n: number, of: number): string {
  if (!of) return '-';
  const p = (n / of) * 100;
  return `${p < 10 ? p.toFixed(1) : Math.round(p)}%`;
}

const OPENED = MONO_SCALE[3];

/**
 * How this automation's emails did. Rates are of delivered emails, or of
 * sent ones until delivery events arrive, as on the Emails overview.
 */
export function EmailSection({ workflowId, steps }: { workflowId: string; steps: WorkflowStep[] }) {
  const v = useVocab();
  const reduced = useReducedMotion() ?? false;
  const [days, setDays] = useState('30');
  const q = useWorkflowEmailStats(workflowId, Number(days));
  const numbers = new Map(allSteps(steps).map((s, i) => [s.id, i + 1]));
  const stepName = (id: string) => (numbers.has(id) ? `Step ${numbers.get(id)}` : 'A removed step');
  const d = q.data;
  const t = d?.totals;
  const base = t ? (t.delivered > 0 ? t.delivered : t.sent) : 0;
  const tiles: [string, string, string][] = t
    ? [
        ['Sent', formatNumber(t.sent), `in ${days} days`],
        ['Delivered', formatNumber(t.delivered), `${rate(t.delivered, t.sent)} of sent`],
        ['Opened', rate(t.opened, base), `${formatNumber(t.opened)} emails`],
        ['Clicked', rate(t.clicked, base), `${formatNumber(t.clicked)} emails`],
        ['Bounced', formatNumber(t.bounced), rate(t.bounced, t.sent)],
        ['Complaints', formatNumber(t.complained), rate(t.complained, base)],
        ['Unsubscribed', formatNumber(t.unsubscribed), rate(t.unsubscribed, base)],
      ]
    : [];
  return (
    <Card padding="sm" withBorder data-testid="workflow-email-stats">
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <div>
            <Text size="sm" fw={600}>Email</Text>
            <Text size="xs" c="dimmed">
              {d && d.campaign_ids.length > 1
                ? `How its emails did, including those sent before it became ${v.a('workflow')}.`
                : `How this ${v.one('workflow')}'s emails did.`}
            </Text>
          </div>
          <SegmentedControl size="xs" value={days} onChange={setDays} data={DAY_RANGES} aria-label="Days" data-testid="email-stats-days" />
        </Group>
        {q.error && <Alert color="red" variant="light">{q.error.message}</Alert>}
        {!d && !q.error && <Group justify="center" p="md"><Loader size="sm" /></Group>}
        {d && t && (
          <>
            <StatGrid columns={7}>
              {tiles.map(([label, value, caption], i) => (
                <div key={label} data-testid="email-stat" data-label={label}>
                  <StatTile label={label} value={0} format={() => value} caption={caption} index={i} />
                </div>
              ))}
            </StatGrid>
            <Box>
              <Group justify="space-between" mb={6} wrap="wrap">
                <Text size="xs" c="dimmed">Per day</Text>
                <Group gap="md">
                  <LegendItem color={CHART_INK} label="Sent" line />
                  <LegendItem color={OPENED} label="Opened" line />
                  <LegendItem color={CHART_POSITIVE} label="Clicked" line />
                </Group>
              </Group>
              {d.daily.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="lg" data-testid="email-daily-empty">Nothing sent in this period.</Text>
              ) : (
                <div role="img" aria-label={`Emails sent, opened and clicked per day over ${days} days`}>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={d.daily.map((x) => ({ ...x, label: x.day.slice(5) }))} margin={{ top: 6, right: 0, bottom: 0, left: -4 }}>
                      <CartesianGrid {...GRID_PROPS} />
                      <XAxis dataKey="label" {...X_AXIS_PROPS} />
                      <YAxis {...Y_AXIS_PROPS} tickFormatter={formatCompactNumber} allowDecimals={false} />
                      <ChartHover
                        cursor={LINE_CURSOR}
                        isAnimationActive={false}
                        content={
                          <ChartTooltip
                            names={{ sent: 'Sent', opened: 'Opened', clicked: 'Clicked' }}
                            colors={{ sent: CHART_INK, opened: OPENED, clicked: CHART_POSITIVE }}
                            formatters={{ sent: formatNumber, opened: formatNumber, clicked: formatNumber }}
                          />
                        }
                      />
                      {([['sent', CHART_INK], ['opened', OPENED], ['clicked', CHART_POSITIVE]] as const).map(([key, color]) => (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={color}
                          strokeWidth={1.75}
                          dot={false}
                          activeDot={{ r: 3.5, stroke: TOKENS.surface, strokeWidth: 2, fill: color }}
                          {...chartAnimation(reduced)}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Box>
            {d.steps.length > 0 && (
              <Table.ScrollContainer minWidth={560}>
                <Table verticalSpacing={6} fz="sm" data-testid="email-steps">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Send step</Table.Th>
                      <Table.Th ta="right">Sent</Table.Th>
                      <Table.Th ta="right">Delivered</Table.Th>
                      <Table.Th ta="right">Opened</Table.Th>
                      <Table.Th ta="right">Clicked</Table.Th>
                      <Table.Th ta="right">Bounced</Table.Th>
                      <Table.Th ta="right">Unsubscribed</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {d.steps.map((st) => {
                      const sb = st.totals.delivered > 0 ? st.totals.delivered : st.totals.sent;
                      return (
                        <Table.Tr key={st.step_id} data-testid="email-step-row">
                          <Table.Td>
                            <Text size="sm">{`${stepName(st.step_id)}: ${st.template_name ? `'${st.template_name}'` : 'a deleted template'}`}</Text>
                            {st.shared_with.length > 0 && (
                              <Text size="xs" c="dimmed" data-testid="email-step-shared">
                                {`Same template as ${st.shared_with.map(stepName).join(' and ')}, so these figures are theirs together.`}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td ta="right" ff="monospace">{formatNumber(st.totals.sent)}</Table.Td>
                          <Table.Td ta="right" ff="monospace">{formatNumber(st.totals.delivered)}</Table.Td>
                          <Table.Td ta="right" ff="monospace">{rate(st.totals.opened, sb)}</Table.Td>
                          <Table.Td ta="right" ff="monospace">{rate(st.totals.clicked, sb)}</Table.Td>
                          <Table.Td ta="right" ff="monospace">{formatNumber(st.totals.bounced)}</Table.Td>
                          <Table.Td ta="right" ff="monospace">{formatNumber(st.totals.unsubscribed)}</Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
            {d.links.length > 0 && (
              <Table.ScrollContainer minWidth={480}>
                <Table verticalSpacing={6} fz="sm" data-testid="email-links">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Link</Table.Th>
                      <Table.Th ta="right">Clicks</Table.Th>
                      <Table.Th ta="right">Emails clicked</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {d.links.map((l) => (
                      <Table.Tr key={`${l.link_name}|${l.url}`} data-testid="email-link-row">
                        <Table.Td style={{ maxWidth: 360 }}>
                          {l.link_name && <Text size="sm" fw={600}>{l.link_name}</Text>}
                          {l.url && (
                            <Anchor href={l.url} target="_blank" rel="noreferrer" size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                              {l.url}
                            </Anchor>
                          )}
                        </Table.Td>
                        <Table.Td ta="right" ff="monospace">{formatNumber(l.clicks)}</Table.Td>
                        <Table.Td ta="right" ff="monospace">{formatNumber(l.unique_clicks)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
            <Text size="xs" c="dimmed">
              Opens read higher than the truth: Apple Mail opens every email itself. Clicks are the better measure.
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}

function ReachList({
  steps, reach, enrolled, depth,
}: { steps: WorkflowStep[]; reach: Map<string, WorkflowStepReach>; enrolled: number; depth: number }) {
  const v = useVocab();
  return (
    <Stack gap={6} pl={depth ? 'md' : 0} style={depth ? { borderLeft: `2px solid ${TOKENS.border}` } : undefined}>
      {steps.map((s) => {
        const r = reach.get(s.id);
        const n = r?.reached ?? 0;
        const c = cfg(s);
        const name = s.kind === 'wait' ? waitSentence(c) : s.kind === 'wait_until' ? waitUntilSentence(v, c) : stepLabel(v, s.kind);
        return (
          <Box key={s.id} data-testid="reach-step" data-step-id={s.id}>
            <Group justify="space-between" wrap="nowrap" gap="sm">
              <Text size="sm" truncate="end" style={{ minWidth: 0 }}>{name}</Text>
              <Group gap={6} wrap="nowrap" style={{ flex: 'none' }}>
                {r?.matched != null && <Badge variant="light" color="chartGreen" radius="sm" tt="none">{`${formatNumber(r.matched)} matched`}</Badge>}
                {r?.timed_out != null && <Badge variant="light" color="gray" radius="sm" tt="none">{`${formatNumber(r.timed_out)} timed out`}</Badge>}
                {r?.passes != null && <Badge variant="light" color="chartBlue" radius="sm" tt="none">{`${formatNumber(r.passes)} ${r.passes === 1 ? 'pass' : 'passes'}`}</Badge>}
                <Text size="sm" fw={600} ff="monospace" data-testid="reach-count">{formatNumber(n)}</Text>
              </Group>
            </Group>
            <Progress value={enrolled ? (n / enrolled) * 100 : 0} size={4} color="chartBlue" mt={4} />
            {s.kind === 'branch' && (
              <Stack gap={6} mt={6}>
                {(['yes', 'no'] as const).map((side) => (
                  <Box key={side}>
                    <Badge variant="light" color={side === 'yes' ? 'chartGreen' : 'gray'} radius="sm" tt="none" mb={4}>
                      {`${side === 'yes' ? 'Yes' : 'No'}: ${formatNumber((side === 'yes' ? r?.yes : r?.no) ?? 0)}`}
                    </Badge>
                    <ReachList steps={s[side] ?? []} reach={reach} enrolled={enrolled} depth={depth + 1} />
                  </Box>
                ))}
              </Stack>
            )}
            {s.kind === 'repeat' && (
              <Box mt={6}>
                <ReachList steps={s.body ?? []} reach={reach} enrolled={enrolled} depth={depth + 1} />
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function Enrolments({ workflowId, canManage }: { workflowId: string; canManage: boolean }) {
  const v = useVocab();
  const e = useEnrolments(workflowId);
  const edits = useEnrolEdits();
  if (!e.data || (e.data.enrolments.length === 0 && e.data.schedules.length === 0)) return null;
  return (
    <Card padding="sm" withBorder data-testid="enrolments">
      <Stack gap="sm">
        <Text size="sm" fw={600}>Enrolled from segments</Text>
        {e.data.schedules.map((s) => (
          <Group key={s.id} justify="space-between" wrap="nowrap" data-testid="enrol-schedule-row">
            <div style={{ minWidth: 0 }}>
              <Text size="sm">{`${s.sentence}: ${s.segment_name}`}</Text>
              <Text size="xs" c="dimmed">
                {s.reenrol ? 'Everyone who matches, every time.' : 'Only those who newly match.'}
                {s.next_run_at ? ` Next: ${new Date(s.next_run_at).toLocaleString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.` : ''}
              </Text>
            </div>
            {canManage && (
              <Tooltip label="Stop this schedule" openDelay={300}>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label="Stop this schedule"
                  onClick={async () => {
                    try {
                      await edits.stopSchedule.mutateAsync({ id: workflowId, scheduleId: s.id });
                      notifications.show({ color: 'teal', title: 'Schedule stopped', message: s.segment_name });
                    } catch (err) {
                      notifications.show({ color: 'red', title: 'Not stopped', message: (err as Error).message });
                    }
                  }}
                >
                  <IconTrash size={15} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        ))}
        {e.data.enrolments.length > 0 && (
          <Table.ScrollContainer minWidth={520}>
            <Table verticalSpacing={6} fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Segment</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Enrolled</Table.Th>
                  <Table.Th ta="right">Already in</Table.Th>
                  <Table.Th ta="right">At limit</Table.Th>
                  <Table.Th ta="right">Seen before</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {e.data.enrolments.map((j) => (
                  <Table.Tr key={j.id}>
                    <Table.Td>
                      <Text size="sm" truncate="end">{j.segment_name}</Text>
                      {j.error && <Text size="xs" c="red">{j.error}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" radius="sm" tt="none" color={j.status === 'done' ? 'chartGreen' : j.status === 'failed' ? 'red' : 'chartAmber'}>
                        {j.status === 'done' ? 'Done' : j.status === 'failed' ? 'Failed' : j.status === 'running' ? 'Enrolling' : 'Queued'}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="right" ff="monospace">{formatNumber(j.enrolled)}</Table.Td>
                    <Table.Td ta="right" ff="monospace">{formatNumber(j.skipped_active)}</Table.Td>
                    <Table.Td ta="right" ff="monospace">{formatNumber(j.skipped_limit)}</Table.Td>
                    <Table.Td ta="right" ff="monospace">{formatNumber(j.skipped_seen)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        <Text size="xs" c="dimmed">{`${v.Many('run')} enrolled from a segment still check the conditions first.`}</Text>
      </Stack>
    </Card>
  );
}
